// @flow
import { actions as catalogActions } from 'state/reducers/catalogReducer';
import type {
  Catalog,
  CreationCatalog,
  DiskPreviewInfo,
  Dispatch,
  GetState,
  MetadataDefinition,
  NamespaceDefinition,
} from 'state/types';
import {
  getCatalogs as getCatalogsSelector,
  getCatalogProperties as getCatalogPropertiesSelector,
  getIdentitySession,
  getLocale,
  getMetadataMappings as getMetadataMappingsSelector,
} from 'state/selectors';
import { goToRoute } from 'util/routeUtils';
import { getEmbedMappingForField } from 'util/fieldsPageUtils';
import { actions as modalPromptActions } from 'portfolio-shared/state/reducers/modalPromptReducer';
import {
  addNotification,
  ERROR_LEVEL,
  SUCCESS_LEVEL,
} from 'portfolio-shared/state/commands/globalNotificationsCommands';
import {
  CATALOG_PROPERTY_KEY,
  CATALOG_STORAGE_TYPE,
  DELETE_VAULT_CATALOG_PANEL_ID,
} from 'util/constants';
import localizedStrings from 'util/localizedStrings';
import { actions as dialogPanelActions } from 'portfolio-shared/state/reducers/dialogPanelReducer';
import type {
  CatalogingOptions,
  ExclusionInfo,
  IngestConfig,
  FieldDefinition,
  MetadataMapping,
  SmartKeywordsSettings,
  UserCatalogInfo,
} from '../types/catalogTypes';
import logger from 'portfolio-shared/util/logging';
import { makeDefaultMappingsForFieldDef } from 'util/fieldsPageUtils';

const { showDialogPanel } = dialogPanelActions;

const {
  addCustomXmpNamespaces: addCustomXmpNamespacesAction,
  addFields: addFieldsAction,
  addNestedValues: addNestedValuesAction,
  addOrUpdateUserCatalogInfo: addOrUpdateUserCatalogInfoAction,
  createCatalog: createCatalogAction,
  createCatalogTemplate: createCatalogTemplateAction,
  createMetadataDefinitions: createMetadataDefinitionsAction,
  deleteMetadataDefinition: deleteMetadataDefinitionAction,
  createMetadataMappings: createMetadataMappingsAction,
  deleteCatalog: deleteCatalogAction,
  deleteCatalogTemplate: deleteCatalogTemplateAction,
  deleteMetadataMappings: deleteMetadataMappingsAction,
  deleteNestedValues: deleteNestedValuesAction,
  getCatalogs: getCatalogsAction,
  getCatalogProperties: getCatalogPropertiesAction,
  getCatalogTypes: getCatalogTypesAction,
  getCustomXmpNamespaces: getCustomXmpNamespacesAction,
  getFields: getFieldsAction,
  getMetadataDefinitions: getMetadataDefinitionsAction,
  getMetadataMappings: getMetadataMappingsAction,
  getUserCatalogInfo: getUserCatalogInfoAction,
  removeAllPredefinedValues: removeAllPredefinedValuesAction,
  removeCustomXmpNamespaces: removeCustomXmpNamespacesAction,
  removeFields: removeFieldsAction,
  removeUsersFromCatalog: removeUsersFromCatalogAction,
  renameCatalog: renameCatalogAction,
  renameCatalogTemplate: renameCatalogTemplateAction,
  restoreFileTypesDefault: restoreFileTypesDefaultAction,
  setExclusionInfo: setExclusionInfoAction,
  setIngestConfig: setIngestConfigAction,
  updateCatalogingOptions: updateCatalogingOptionsAction,
  updateDiskPreviewInfo: updateDiskPreviewInfoAction,
  updateFields: updateFieldsAction,
  updatePredefinedList: updatePredefinedListAction,
  updateSmartKeywordsSettings: updateSmartKeywordsSettingsAction,
} = catalogActions;

const { showModalPrompt } = modalPromptActions;

export function getCatalogs() {
  return async function getCatalogs(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(getCatalogsAction(session));
    }
  };
}

export function getCatalogTypes() {
  return async function getCatalogTypes(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    const locale = getLocale(getState());
    if (session) {
      await dispatch(getCatalogTypesAction(session, locale));
    }
  };
}

export function getCatalogProperties(catalogId: string, properties: string[]) {
  return async function getCatalogProperties(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        getCatalogPropertiesAction(session, catalogId, properties)
      );
    }
  };
}

export function createCatalogTemplate(
  catalogId: string,
  templateName: string,
  successMessage: string
) {
  return async function createCatalogTemplate(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        createCatalogTemplateAction(session, catalogId, templateName)
      );
      dispatch(addNotification(successMessage, SUCCESS_LEVEL));
      await dispatch(getCatalogTypesAction(session, catalogId));
    }
  };
}

export function restoreFileTypesDefault(catalogId: string) {
  return async function restoreFileTypesDefault(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(restoreFileTypesDefaultAction(session, catalogId));
      await dispatch(
        getCatalogPropertiesAction(session, catalogId, [
          CATALOG_PROPERTY_KEY.CATALOGING_OPTIONS,
          CATALOG_PROPERTY_KEY.EXCLUSION_INFO,
          CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
        ])
      );
    }
  };
}

export function createCatalog(
  catalog: CreationCatalog,
  createWatchFolder: boolean,
  watchFolderNamePartFiles: string
) {
  return async function createCatalog(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        createCatalogAction(
          session,
          catalog,
          createWatchFolder,
          watchFolderNamePartFiles
        )
      );
      await dispatch(getCatalogsAction(session));
      const catalogs = getCatalogsSelector(getState());
      const catalogToSelect = catalogs.find(cat => cat.name === catalog.name);
      if (catalogToSelect) {
        goToRoute(`catalogs/${catalogToSelect.catalogId}/general`);
      }
    }
  };
}

export function deleteCatalog(catalog: Catalog) {
  return async function deleteCatalog(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());

    async function doDelete() {
      if (session) {
        await dispatch(deleteCatalogAction(session, catalog));
        await dispatch(getCatalogsAction(session));
      }
    }

    const catProperties = getCatalogPropertiesSelector(getState());
    const storageType =
      catProperties &&
      catProperties.catalogDetails &&
      catProperties.catalogDetails.storageType;

    switch (storageType) {
      case CATALOG_STORAGE_TYPE.FILESYSTEM:
        dispatch(
          showModalPrompt({
            message: localizedStrings.CATALOG_DELETE_CONFIRM,
            onButton2: doDelete,
            dataTestName: 'delete-catalog-modal',
          })
        );
        break;
      case CATALOG_STORAGE_TYPE.VAULT:
        dispatch(showDialogPanel(DELETE_VAULT_CATALOG_PANEL_ID, { doDelete }));
        break;
      default:
        logger.error(
          'Attempted to delete catalog with unknown type: ',
          storageType
        );
    }
  };
}

export function renameCatalog(catalog: Catalog, successMessage: string) {
  return async function renameCatalog(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    if (session) {
      const {
        payload: { name },
      } = await dispatch(renameCatalogAction(session, catalog));
      dispatch(getCatalogsAction(session));
      dispatch(addNotification(successMessage, SUCCESS_LEVEL));
      return name;
    }
  };
}

export function deleteCatalogTemplate(templateId: string) {
  return async function deleteCatalogTemplate(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(deleteCatalogTemplateAction(session, templateId));
      dispatch(getCatalogTypesAction(session, getLocale(getState())));
    }
  };
}

export function renameCatalogTemplate(templateId: string, name: string) {
  return async function renameCatalogTemplate(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(renameCatalogTemplateAction(session, templateId, name));
      dispatch(getCatalogTypesAction(session, getLocale(getState())));
    }
  };
}

export function updateDiskPreviewInfo(
  catalogId: string,
  dpi: DiskPreviewInfo,
  successMessage: string
) {
  return async function updateDiskPreviewInfo(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(updateDiskPreviewInfoAction(session, catalogId, dpi));
      await dispatch(
        getCatalogProperties(catalogId, [
          CATALOG_PROPERTY_KEY.DISK_PREVIEW_INFO,
        ])
      );
      dispatch(addNotification(successMessage, SUCCESS_LEVEL));
    }
  };
}

export function getFields(catalogId: string) {
  return async function getFields(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(getFieldsAction(session, catalogId));
    }
  };
}

export function addFields(
  catalogId: string,
  fields: FieldDefinition[],
  metadataDefIdToMap: ?string
) {
  return async function addFields(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    if (!session) {
      return undefined;
    }
    const {
      payload: [newFieldDef],
    } = await dispatch(addFieldsAction(session, catalogId, fields));
    await dispatch(getFields(catalogId));
    if (metadataDefIdToMap) {
      const mappings = makeDefaultMappingsForFieldDef(
        newFieldDef,
        metadataDefIdToMap
      );
      await dispatch(createMetadataMappings(catalogId, mappings));
    }
  };
}

export function updateFields(catalogId: string, fields: FieldDefinition[]) {
  return async function udpateFields(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    if (session) {
      let error;
      await dispatch(updateFieldsAction(session, catalogId, fields)).catch(
        e => {
          error = e;
        }
      );
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      }
      await dispatch(getFields(catalogId));
    }
  };
}

export function removeFields(
  catalogId: string,
  fieldIds: string[],
  isDisable: boolean = false
) {
  return async function removeFields(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        showModalPrompt({
          message: isDisable
            ? localizedStrings.FIELD_DISABLE_CONFIRM_MSG
            : localizedStrings.FIELD_DELETE_CONFIRM_MSG,
          dataTestName: 'delete-field-modal',
          onButton2: async function doDelete() {
            await dispatch(removeFieldsAction(session, catalogId, fieldIds));
            await dispatch(getFields(catalogId));
          },
        })
      );
    }
  };
}

export function removeAllPredefinedValues(
  catalogId: string,
  fieldIds: string[]
) {
  return async function removeAllPredefinedValues(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      let error;
      await dispatch(
        removeAllPredefinedValuesAction(session, catalogId, fieldIds)
      ).catch(e => {
        error = e;
      });
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      }
      await dispatch(getFields(catalogId));
    }
  };
}

export function updatePredefinedList(
  catalogId: string,
  fieldDef: FieldDefinition,
  valuesToAdd: any,
  valuesToRemove: any,
  parentNestedValueId?: string
) {
  return async function updatePredefinedList(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      if (fieldDef.isHierarchical) {
        if (valuesToAdd) {
          await dispatch(
            addNestedValuesAction(
              session,
              catalogId,
              fieldDef.id,
              valuesToAdd,
              parentNestedValueId
            )
          );
        }
        if (valuesToRemove) {
          await dispatch(
            deleteNestedValuesAction(
              session,
              catalogId,
              fieldDef.id,
              valuesToRemove
            )
          );
        }
      } else {
        await dispatch(
          updatePredefinedListAction(
            session,
            catalogId,
            fieldDef.id,
            valuesToAdd,
            valuesToRemove
          )
        );
      }
      // This over-fetches, we may want to change the admin client to
      // just fetch one field (which the server API supports)
      await dispatch(getFieldsAction(session, catalogId));
    }
  };
}

export function getMetadataDefinitions() {
  return async function getMetadataDefinitions(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(getMetadataDefinitionsAction(session));
    }
  };
}

export function createMetadataDefinitions(
  metadataDefinitions: MetadataDefinition[]
) {
  return async function createMetadataDefinitions(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        createMetadataDefinitionsAction(session, metadataDefinitions)
      );
      await dispatch(getMetadataDefinitionsAction(session));
    }
  };
}

export function deleteMetadataDefinition(metadataDefinitionId: string) {
  return async function deleteMetadataDefinition(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      dispatch(
        showModalPrompt({
          message: localizedStrings.FIELD_METADATA_DELETE_CONFIRM_MSG,
          onButton2: async () => {
            await dispatch(
              deleteMetadataDefinitionAction(session, metadataDefinitionId)
            );
            await dispatch(getMetadataDefinitionsAction(session));
          },
          dataTestName: 'delete-metadata-modal',
        })
      );
    }
  };
}

export function getMetadataMappings(catalogId: string) {
  return async function getMetadataMappings(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(getMetadataMappingsAction(session, catalogId));
    }
  };
}

export function createMetadataMappings(
  catalogId: string,
  mappings: MetadataMapping[]
) {
  return async function createMetadataMappings(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        createMetadataMappingsAction(session, catalogId, mappings)
      );
      await dispatch(getMetadataMappingsAction(session, catalogId));
    }
  };
}

export function deleteMetadataMappings(
  catalogId: string,
  mappings: MetadataMapping[]
) {
  return async function deleteMetadataMappings(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        deleteMetadataMappingsAction(session, catalogId, mappings)
      );
      await dispatch(getMetadataMappingsAction(session, catalogId));
    }
  };
}

export function setEmbedMetadataMapping(
  catalogId: string,
  fieldId: string,
  mapping: MetadataMapping
) {
  return async function setEmbedMetadataMapping(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    const allMappings = getMetadataMappingsSelector(getState());
    const oldMapping = getEmbedMappingForField(fieldId, allMappings);
    if (session) {
      oldMapping &&
        (await dispatch(
          deleteMetadataMappingsAction(session, catalogId, [oldMapping])
        ));
      await dispatch(
        createMetadataMappingsAction(session, catalogId, [mapping])
      );
      await dispatch(getMetadataMappingsAction(session, catalogId));
    }
  };
}

export function addOrUpdateUserCatalogInfo(
  infos: UserCatalogInfo[],
  catalogName: string
) {
  return async function addOrUpdateUserCatalogInfo(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(addOrUpdateUserCatalogInfoAction(session, infos));
      await dispatch(getUserCatalogInfoAction(session, catalogName));
    }
  };
}

export function removeUsersFromCatalog(
  catalogName: string,
  userNames: string[]
) {
  return async function removeUsersFromCatalog(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        removeUsersFromCatalogAction(session, catalogName, userNames)
      );
      await dispatch(getUserCatalogInfoAction(session, catalogName));
    }
  };
}

export function updateCatalogingOptions(
  catalogId: string,
  catOptions: CatalogingOptions
) {
  return async function updateCatalogingOptions(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        updateCatalogingOptionsAction(session, catalogId, catOptions)
      );
      await dispatch(
        getCatalogProperties(catalogId, [
          CATALOG_PROPERTY_KEY.CATALOGING_OPTIONS,
          CATALOG_PROPERTY_KEY.EXCLUSION_INFO,
          CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
        ])
      );
    }
  };
}

export function setExclusionInfo(
  catalogId: string,
  exclusionInfo: ExclusionInfo
) {
  return async function setExclusionInfo(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(setExclusionInfoAction(session, catalogId, exclusionInfo));
      await dispatch(
        getCatalogPropertiesAction(session, catalogId, [
          CATALOG_PROPERTY_KEY.CATALOGING_OPTIONS,
          CATALOG_PROPERTY_KEY.EXCLUSION_INFO,
          CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
        ])
      );
    }
  };
}

export function setIngestConfig(
  catalogId: string,
  catOptions: CatalogingOptions,
  ingestConfig: IngestConfig
) {
  return async function setIngestConfig(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await Promise.all([
        dispatch(setIngestConfigAction(session, catalogId, ingestConfig)),
        dispatch(updateCatalogingOptionsAction(session, catalogId, catOptions)),
      ]);
      await dispatch(
        getCatalogPropertiesAction(session, catalogId, [
          CATALOG_PROPERTY_KEY.CATALOGING_OPTIONS,
          CATALOG_PROPERTY_KEY.INGEST_CONFIG,
          CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
        ])
      );
    }
  };
}

export function updateSmartKeywordsSettings(
  catalogId: string,
  smartKeywordsSettings: SmartKeywordsSettings
) {
  return async function updateSmartKeywordsSettings(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(
        updateSmartKeywordsSettingsAction(
          session,
          catalogId,
          smartKeywordsSettings
        )
      );
      await dispatch(
        getCatalogPropertiesAction(session, catalogId, [
          CATALOG_PROPERTY_KEY.SMART_KEYWORDS_SETTINGS,
        ])
      );
    }
  };
}

export function getCustomXmpNamespaces() {
  return async function getCustomXmpNamespaces(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      return await dispatch(getCustomXmpNamespacesAction(session));
    }
  };
}

export function removeCustomXmpNamespaces(namespaceIds: string[]) {
  return async function removeCustomXmpNamespaces(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      dispatch(
        showModalPrompt({
          message: localizedStrings.FIELD_NAMESPACE_REMOVE_CONFIRMATION,
          onButton2: async () => {
            await dispatch(
              removeCustomXmpNamespacesAction(session, namespaceIds)
            );
            await dispatch(getCustomXmpNamespacesAction(session));
          },
          dataTestName: 'delete-namespace-modal',
        })
      );
    }
  };
}

export function addCustomXmpNamespaces(namespaces: NamespaceDefinition[]) {
  return async function addCustomXmpNamespaces(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    if (session) {
      await dispatch(addCustomXmpNamespacesAction(session, namespaces));
      return await dispatch(getCustomXmpNamespacesAction(session));
    }
  };
}



// WEBPACK FOOTER //
// ./src/state/commands/catalogCommands.js